/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, Suspense, lazy, useEffect, useState} from 'react'
import {Routes, Route, BrowserRouter, Navigate, Outlet, useSearchParams} from 'react-router-dom'
import {PrivateRoutes, SuspensedView} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App} from '../App'
import {InvoicePrintModalPrintWrapper} from '../modules/apps/invoice-management/invoices-list/invoice-print-modal/InvoicePrintModalPrintWrapper'
import StuffidsPage from '../modules/apps/stuffid-management/StuffidsPage'
import {isStuffIdHostname} from '../modules/apps/stuffid-management/stuffidSite'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import {SamanIPGCallback} from '../modules/cb/SamanIPGCallback'
import {SamanIPGfwd} from '../modules/cb/SamanIPGfwd'
import {
  API_URL,
  isAPP,
  isMegaPay,
  isPOS,
  toAbsoluteUrl,
  URL,
  WebSite,
  WithChildren,
} from '../../_metronic/helpers'
import {
  CompanyInquiryEntry,
  PersonalTaxFileInquiryEntry,
} from '../modules/extraService/inquiry/CompanyInquiryEntry'
import {PosResult} from '../modules/cb/PosResult'
import {RegisterForm} from '../modules/apps/registerForm/RegisterForm'
import {NewTicketPublic} from '../modules/apps/ticket-management/NewTicketPublic'
import {CAPTURE_PATH} from '../modules/apps/ticket-management/screen-share/capture-window'
import {SupportSsoCallback} from '../modules/auth/components/SupportSsoCallback'
import {StaffSsoCallback} from '../modules/auth/components/StaffSsoCallback'
import {TaxFolderSsoCallback} from '../modules/auth/components/TaxFolderSsoCallback'
import {AppLoading} from '../../_metronic/helpers/components/AppLoading'
import {Navbar} from '../../_metronic/layout/components/header/Navbar'
import {SimpleFooter} from '../pages/dashboard/Simplefooter'
import {AppBackdrop} from '../theme/AppBackdrop'
import {BrandLockup} from '../theme/BrandLogo'
import axios from 'axios'
import {canUseTicketing} from '../modules/auth/core/ticketingAccess'
import {getEntryPath} from '../modules/auth/core/postLoginPath'
import {rememberLoginReturn} from '../modules/auth/core/loginReturn'

const {PUBLIC_URL} = process.env
const AccountingSyncDocumentationPage = lazy(
  () => import('../modules/public/accounting-sync-docs/AccountingSyncDocumentationPage')
)
// The buyer's link of a shared invoice (/s/{token}) and the result page a payment without a page returns to.
const InvoiceSharePage = lazy(() => import('../modules/online-payment/share/InvoiceSharePage'))
const PaymentResultPage = lazy(() => import('../modules/online-payment/components/PaymentResultPage'))
// The public print page of an invoice: opens without signing in (the QR code on the paper links here).
const InvoicePrintPage = lazy(
  () => import('../modules/apps/invoice-management/invoice-print/InvoicePrintPage')
)
// A partner's sign-up link (/join/{token}): opens without signing in; the taxpayer signs in on the page.
const PartnerOnboardingPage = lazy(() => import('../modules/partner-onboarding/PartnerOnboardingPage'))
// «نمایش و ضبط صفحه»: the small window that holds a ticket's screen share and recording past a refresh of the panel.
const ScreenCapturePage = lazy(
  () => import('../modules/apps/ticket-management/screen-share/ScreenCapturePage')
)

const EntryUrl = () => {
  const Mno = window.location.href.includes('Mno')
  const {setCurrentUser, saveAuth} = useAuth()
  const [loggedIn, setLoggedIn] = useState<boolean | undefined>()
  // Read while rendering: the sign-in page's redirect below runs first. Only a new ticket's request is kept.
  useState(() => rememberLoginReturn(window.location.pathname + window.location.search))

  useEffect(() => {
    if (Mno) {
      const queryParameters = new URLSearchParams(window.location.search)
      const _versionId = queryParameters.get('VersionId')
      const _mno = queryParameters.get('Mno')
      axios
        .post(`${API_URL}/auth/toplogin`, {VersionId: Number(_versionId), Mno: _mno})
        .then((response) => {
          saveAuth(response.data)
          setCurrentUser(response.data)
          setLoggedIn(true)
        })
        .catch(() => {
          setTimeout(() => {
            setLoggedIn(false)
          }, 2000)
          setTimeout(() => {
            window.location.reload()
          }, 10000)
        })
    }
  }, [])

  return (
    <>
      <div className='text-center p-5 fs-3x'>
        <a href={(isAPP ? URL : WebSite) || '/' || '/'} target='_blank' className='mb-12'>
          <img alt='Logo' src={toAbsoluteUrl('/media/logos/default.svg')} className='h-100px' />
        </a>
        <div className='p-5'>
          {Mno ? (
            <>
              {loggedIn == undefined && (
                <div>
                  <i className='las la-spinner fs-3x la-spin text-primary'></i>
                </div>
              )}
              {loggedIn == false && (
                <>
                  <div className='text-danger'>ورود نا موفق</div>
                  <div className='fs-8 ltr text-gray-300'>{window.location.href}</div>
                </>
              )}
              {loggedIn && (
                <>
                  <div className='text-success'>ورود موفق</div>
                  <Navigate to='/auth' />
                </>
              )}
            </>
          ) : (
            <Navigate to='/auth' />
          )}
        </div>
      </div>
    </>
  )
}

const PaymentPlace = () => {
  return (
    <div className='w-100 text-center p-5'>
      <div className='card mw-750px m-auto p-5 rounded shadow'>
        <div className='card-body'>
          <span className='my-2 w-100 text-center'>
            <img
              alt='Logo'
              src={toAbsoluteUrl('/media/illustrations/misc/credit-card.png')}
              className='h-175px'
            />
          </span>
          <App />
        </div>
      </div>
    </div>
  )
}

const AppRoutes: FC = () => {
  const {currentUser} = useAuth()
  const isStuffIdDomain = isStuffIdHostname(window.location.hostname)

  // stuffid.asatsp.ir is the public goods/services ID search: every address on it opens the search,
  // signed in or not, and never the sign-in page.
  if (isStuffIdDomain) {
    return (
      <BrowserRouter basename={PUBLIC_URL}>
        <Routes>
          <Route element={<App />}>
            <Route
              path='*'
              element={
                <SuspensedView>
                  <StuffidsPage />
                </SuspensedView>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    )
  }

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      {/* <Online>You're online right now.</Online> */}
      <Routes>
        <Route
          path='docs/accounting-sync'
          element={
            <Suspense fallback={<AppLoading mode='page' message='در حال بارگذاری مستندات…' />}>
              <AccountingSyncDocumentationPage />
            </Suspense>
          }
        />
        <Route element={<App />}>
          <Route element={<PaymentPlace />}>
            <Route path='cb/samanipg' element={<SamanIPGCallback />} />
            <Route path='fwd/samanipg' element={<SamanIPGfwd />} />
            {/* <Route path='paymentresult' element={<PosResult />} /> */}
          </Route>
          <Route
            path='ticket'
            element={
              currentUser && !canUseTicketing(currentUser) ? (
                <Navigate to='/dashboard' replace />
              ) : currentUser ? (
                // Signed in: straight to the new ticket, with the request its address names (it was a dead end).
                <Navigate to={`/apps/ticket-management/new${window.location.search}`} replace />
              ) : (
                <SuspensedViewPublic>
                  <NewTicketPublic />
                </SuspensedViewPublic>
              )
            }
          />
          {/* Its own look: the public page in the site's look, or the panel's page for a business user. */}
          <Route path='companyInquery' element={<CompanyInquiryEntry />} />
          {/* The personal tax-file inquiry's own page (asatsp.ir links here), never redirected. */}
          <Route path='tax-file-inquiry' element={<PersonalTaxFileInquiryEntry />} />
          <Route
            path='register/:from'
            element={
              <SuspensedViewPublic showTicketLink={false}>
                <RegisterForm />
              </SuspensedViewPublic>
            }
          />
        </Route>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          <Route
            path='apps/stuffid-management/*'
            element={
              <SuspensedView>
                <StuffidsPage />
              </SuspensedView>
            }
          />
          <Route path='/invoicePrint/:hash' element={<InvoicePrintModalPrintWrapper />} />
          <Route
            path='/invoice/:hash'
            element={
              <Suspense fallback={<AppLoading mode='page' message='در حال بارگذاری صورتحساب…' />}>
                <InvoicePrintPage />
              </Suspense>
            }
          />
          <Route
            path='/s/:token'
            element={
              <Suspense fallback={<AppLoading mode='page' message='در حال بارگذاری صورتحساب…' />}>
                <InvoiceSharePage />
              </Suspense>
            }
          />
          <Route
            path='/payment/result'
            element={
              <Suspense fallback={<AppLoading mode='page' message='در حال دریافت نتیجهٔ پرداخت…' />}>
                <PaymentResultPage />
              </Suspense>
            }
          />
          <Route
            path='/join/:token'
            element={
              <Suspense fallback={<AppLoading mode='page' message='در حال باز کردن لینک ثبت‌نام…' />}>
                <PartnerOnboardingPage />
              </Suspense>
            }
          />
          <Route
            path={CAPTURE_PATH}
            element={
              <Suspense fallback={<AppLoading mode='page' message='در حال آماده‌سازی…' />}>
                <ScreenCapturePage />
              </Suspense>
            }
          />
          <Route path='/supportsso' element={<SupportSsoCallback />} />
          {/* The CRM's «پنل» button: staff enter with the sections their CRM role grants (docs/staff-access.md). */}
          <Route path='/staffsso' element={<StaffSsoCallback />} />
          <Route path='/ssocb' element={<TaxFolderSsoCallback />} />
          <Route
            index
            element={
              currentUser ? <Navigate to={getEntryPath(currentUser)} replace /> : <EntryUrl />
            }
          />

          {currentUser ? (
            <Route path='/*' element={<PrivateRoutes />} />
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<EntryUrl />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

const SuspensedViewPublic: FC<WithChildren & {showTicketLink?: boolean}> = ({
  children,
  showTicketLink,
}) => {
  // Public pages (registration request, public ticket) share the sign-in
  // page's look: the site's backdrop, the logo bar and one white tile.
  return (
    <Suspense fallback={<AppLoading mode='page' message='در حال بارگذاری صفحه…' />}>
      <div className='asa-public-page'>
        <AppBackdrop site />
        <header className='asa-public-page__bar'>
          <BrandLockup href={(isAPP ? URL : WebSite) || '/'} />
          <div
            id='kt_app_header_wrapper'
            className='d-flex align-items-stretch justify-content-between'
          >
            <Navbar />
          </div>
        </header>
        <main className='card asa-public-page__tile'>
          <div className='card-body'>{children}</div>
        </main>
        <footer className='asa-public-page__footer'>
          <SimpleFooter showTicketLink={showTicketLink} />
        </footer>
      </div>
    </Suspense>
  )
}

export {AppRoutes}
